/* eslint-disable max-lines */
import {
  CamAction,
  EscalationState,
  GridConfig,
  LLMEvent,
  UnifiedAlarmGroup,
  VisionEventType,
  VisionOutboundType,
} from '@hakimo-ui/hakimo/types';
import { DynamicGrid, DynamicGridRef } from '@hakimo-ui/hakimo/ui-elements';
import { trackEvent, useScanWs, useUser } from '@hakimo-ui/hakimo/util';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useScanWsMessageHandler } from '../../hooks/useScanWsMessageHandler';

import { AlarmGroupCell } from '@hakimo-ui/hakimo/feature-shared';
import {
  ALARM_COUNT_REQUEST_INTERVAL,
  ALARM_REQUEST_INTERVAL,
} from '../../config';
import InvestigateViewModal from './investigate-view/InvestigateViewModal';

export interface ScanAlarmsGridRef {
  addAlarmGroups: (alarmGroupIds: string[]) => void;
  getActiveGroups: () => string[];
}

interface Props {
  alarmGroupIdToDetailsMap: Record<string, UnifiedAlarmGroup>;
  gridConfig: GridConfig;
  onCellAction: (
    cameraId: string,
    alarmGroupId: string,
    cameraGroupId: string,
    actionType: CamAction,
    comment?: string
  ) => void;
  isScanPaused: boolean;
  activeEscalations: EscalationState[];
  onResolveEscalation: (comment: string, alarmGroupId: string) => void;
}

const ScanAlarmsGrid = forwardRef<ScanAlarmsGridRef, Props>(
  (props: Props, ref) => {
    const {
      alarmGroupIdToDetailsMap,
      gridConfig,
      onCellAction,
      isScanPaused,
      activeEscalations,
      onResolveEscalation,
    } = props;
    const { send: sendScanWS } = useScanWs();
    const user = useUser();
    const dynamicGridRef = useRef<DynamicGridRef>(null);
    const trackedAlarmGroupsRef = useRef<Set<string>>(new Set());
    const [showInvestigationModal, setShowInvestigationModal] = useState(false);
    const [
      alarmGroupIdToShowInvestigationModal,
      setAlarmGroupIdToShowInvestigationModal,
    ] = useState<string | null>(null);
    const requestAlarms = useCallback(
      (count: number) => {
        if (!isScanPaused) {
          sendScanWS?.({
            count: count,
            operator_id: user.email,
            event_type: VisionOutboundType.LIST_ALARMS,
          });
        }
      },
      [isScanPaused, sendScanWS, user.email]
    );

    const [alarmGroupIdToLLMEventMap, setAlarmGroupIdToLLMEventMap] = useState<
      Map<string, LLMEvent>
    >(new Map());

    const handleLLMevents = (data: LLMEvent[]) => {
      const updatedMap = new Map<string, LLMEvent>(alarmGroupIdToLLMEventMap);
      data.forEach((ev) => {
        updatedMap.set(ev.alarm_group_id, ev);
      });

      setAlarmGroupIdToLLMEventMap(updatedMap);
    };

    useScanWsMessageHandler(VisionEventType.LLM_EVENT, handleLLMevents);

    const requestAlarmCount = useCallback(() => {
      sendScanWS?.({
        event_type: VisionOutboundType.SCAN_ALARM_COUNT,
      });
    }, [sendScanWS]);

    useEffect(() => {
      const intervalId = window.setInterval(() => {
        requestAlarmCount();
      }, ALARM_COUNT_REQUEST_INTERVAL);

      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }, [requestAlarmCount]);

    // Track when alarm groups are actually rendered
    useEffect(() => {
      const currentItems = dynamicGridRef.current?.getAllItems() ?? [];

      currentItems.forEach((alarmGroupId) => {
        if (!trackedAlarmGroupsRef.current.has(alarmGroupId)) {
          const alarmGroupDetails = alarmGroupIdToDetailsMap[alarmGroupId];
          if (alarmGroupDetails) {
            trackEvent('scan_alarm_group_rendered', {
              alarm_group_id: alarmGroupId,
              tenant_id: alarmGroupDetails.tenantId,
              severity: alarmGroupDetails.severity,
              username: user.email,
            });

            trackedAlarmGroupsRef.current.add(alarmGroupId);
          }
        }
      });
      const shownItemCount = currentItems.length;
      const alarmGroupDataCount = Object.keys(alarmGroupIdToDetailsMap).length;
      if (shownItemCount > 0 && alarmGroupDataCount === 0) {
        trackEvent('scan_alarm_rendered_mismatch', {
          shown_item_count: shownItemCount,
          alarm_group_data_count: alarmGroupDataCount,
          username: user.email,
        });
      }
    }, [alarmGroupIdToDetailsMap, user.email]);

    useEffect(() => {
      if ((dynamicGridRef.current?.getAllItems().length ?? 0) === 0) {
        requestAlarms(gridConfig.cols * gridConfig.rows - 1);
      }

      const intervalId = window.setInterval(() => {
        const maxGroupCount = gridConfig.cols * gridConfig.rows - 1;
        const currentGroupCount =
          dynamicGridRef.current?.getAllItems().length ?? 0;
        const availableSpace = maxGroupCount - currentGroupCount;
        if (availableSpace > 0) {
          requestAlarms(availableSpace);
        }
      }, ALARM_REQUEST_INTERVAL);

      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }, [gridConfig.cols, gridConfig.rows, requestAlarms]);

    const addAlarmGroups = (alarmGroupIds: string[]) => {
      try {
        dynamicGridRef.current?.addItems(alarmGroupIds);
      } catch (error) {
        trackEvent('scan_error_adding_alarm_group', {
          error: error,
        });
      }
    };

    const removeAlarmGroupFromGrid = (alarmGroupId: string) => {
      dynamicGridRef.current?.removeItems([alarmGroupId]);
      // Remove from tracked alarm groups when removed from grid
      trackedAlarmGroupsRef.current.delete(alarmGroupId);
    };

    const getActiveGroups = () => {
      return dynamicGridRef.current?.getAllItems() ?? [];
    };

    useImperativeHandle(ref, () => ({
      addAlarmGroups,
      getActiveGroups,
    }));

    const onGroupAction = (
      cameraId: string,
      alarmGroupId: string,
      cameraGroupId: string,
      actionType: CamAction,
      comment?: string
    ) => {
      switch (actionType) {
        case CamAction.SAFE:
          trackEvent('event_marked_safe', {
            alarmGroupId: alarmGroupId,
            tenantId: alarmGroupIdToDetailsMap[alarmGroupId].tenantId,
          });
          removeAlarmGroupFromGrid(alarmGroupId);
          setAlarmGroupIdToLLMEventMap((prev) => {
            const updatedMap = new Map<string, LLMEvent>(prev);
            updatedMap.delete(alarmGroupId);
            return updatedMap;
          });
          break;
        case CamAction.ESCALATE:
          trackEvent('event_escalated', {
            alarmGroupId: alarmGroupId,
            tenantId: alarmGroupIdToDetailsMap[alarmGroupId].tenantId,
          });
          break;
        case CamAction.INVESTIGATE: {
          trackEvent('event_investigate', {
            alarmGroupId: alarmGroupId,
            tenantId: alarmGroupIdToDetailsMap[alarmGroupId].tenantId,
          });
          setShowInvestigationModal(true);
          setAlarmGroupIdToShowInvestigationModal(alarmGroupId);
          break;
        }
        default:
      }
      onCellAction(cameraId, alarmGroupId, cameraGroupId, actionType, comment);
    };

    const handleModalClose = () => {
      setShowInvestigationModal(false);
      setAlarmGroupIdToShowInvestigationModal(null);
    };

    const handleCamActionFromInvestigationModal =
      (alarmGroupId: string) =>
      (actionType: CamAction) =>
      (comment?: string) => {
        const alarmGroupDetails = alarmGroupIdToDetailsMap[alarmGroupId];
        onGroupAction(
          alarmGroupDetails.alarms[0].cameraId,
          alarmGroupId,
          alarmGroupDetails.alarms[0].cameraGroupId,
          actionType,
          comment
        );
      };

    const handleResolveEscalation =
      (alarmGroupId: string) => (comment?: string) => {
        setShowInvestigationModal(false);
        removeAlarmGroupFromGrid(alarmGroupId);
        comment && onResolveEscalation(comment, alarmGroupId);
        setAlarmGroupIdToLLMEventMap((prev) => {
          const updatedMap = new Map<string, LLMEvent>(prev);
          updatedMap.delete(alarmGroupId);
          return updatedMap;
        });
      };

    return (
      <>
        <DynamicGrid
          ref={dynamicGridRef}
          maxCols={gridConfig.cols}
          maxRows={gridConfig.rows}
          renderItem={(alarmGroupId, isTopRightCorner) =>
            alarmGroupIdToDetailsMap[alarmGroupId] ? (
              <AlarmGroupCell
                alarmGroup={alarmGroupIdToDetailsMap[alarmGroupId]}
                onGroupAction={onGroupAction}
                isTopRightCorner={isTopRightCorner}
                escalationState={activeEscalations.find(
                  (escalation) => escalation.alarmGroupId === alarmGroupId
                )}
                llmEvent={alarmGroupIdToLLMEventMap.get(alarmGroupId)}
              />
            ) : null
          }
        />

        {showInvestigationModal && alarmGroupIdToShowInvestigationModal && (
          <InvestigateViewModal
            alarmGroup={
              alarmGroupIdToDetailsMap[alarmGroupIdToShowInvestigationModal]
            }
            onClose={handleModalClose}
            handleCamAction={handleCamActionFromInvestigationModal(
              alarmGroupIdToShowInvestigationModal
            )}
            escalationState={activeEscalations.find(
              (escalation) =>
                escalation.alarmGroupId === alarmGroupIdToShowInvestigationModal
            )}
            onCreateEscalation={handleCamActionFromInvestigationModal(
              alarmGroupIdToShowInvestigationModal
            )(CamAction.ESCALATE)}
            onResolveEscalation={handleResolveEscalation(
              alarmGroupIdToShowInvestigationModal
            )}
          />
        )}
      </>
    );
  }
);

export default ScanAlarmsGrid;
